import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer';
import {
  DreResult,
  TaxEntry,
  DreSaleDetailRow,
  DreExpenseDetailRow,
  DreMovementDetailRow,
} from './DrePage';

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

const formatCurrency = (value: unknown): string => {
  const n = toNum(value);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatCurrencyDeduction = (value: unknown): string =>
  `(${formatCurrency(value)})`;

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 9,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 14,
    borderBottomWidth: 2,
    borderBottomColor: '#1a1a1a',
  },
  logo: {
    width: 140,
    height: 48,
    objectFit: 'contain',
  },
  headerRight: {
    flex: 1,
    marginLeft: 20,
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: '#555',
  },
  period: {
    marginBottom: 16,
    padding: 10,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
  },
  periodText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#333',
  },
  table: {
    marginTop: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    paddingVertical: 5,
    paddingHorizontal: 6,
  },
  tableRowHeader: {
    backgroundColor: '#1a1a1a',
    flexDirection: 'row',
    paddingVertical: 7,
    paddingHorizontal: 6,
  },
  tableCellDesc: {
    flex: 1,
    fontSize: 9,
  },
  tableCellValue: {
    width: 100,
    fontSize: 9,
    textAlign: 'right',
  },
  tableCellHeader: {
    flex: 1,
    fontSize: 9,
    fontWeight: 'bold',
    color: '#fff',
  },
  tableCellHeaderValue: {
    width: 100,
    fontSize: 9,
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'right',
  },
  sectionTitle: {
    fontWeight: 'bold',
    paddingVertical: 4,
  },
  indent1: {
    paddingLeft: 12,
  },
  indent2: {
    paddingLeft: 20,
  },
  totalRow: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
    paddingVertical: 7,
  },
  resultRow: {
    backgroundColor: '#1a1a1a',
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  resultLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#fff',
  },
  resultValuePositive: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#4caf50',
  },
  resultValueNegative: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#ff5252',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    fontSize: 8,
    color: '#888',
    textAlign: 'center',
  },
  detailSectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 18,
    marginBottom: 8,
    color: '#1a1a1a',
  },
  vehicleBlock: {
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    padding: 8,
  },
  vehicleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  vehicleTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    flex: 1,
  },
  vehicleMeta: {
    fontSize: 8,
    color: '#666',
    marginBottom: 6,
  },
  miniRow: {
    flexDirection: 'row',
    paddingVertical: 2,
  },
  miniCell: {
    fontSize: 8,
    color: '#333',
  },
  miniCellFlex: {
    flex: 1,
    fontSize: 8,
    color: '#333',
  },
  miniCellValue: {
    width: 80,
    fontSize: 8,
    textAlign: 'right',
  },
  miniHeader: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#555',
    marginTop: 4,
    marginBottom: 2,
  },
  profitPositive: {
    color: '#2e7d32',
    fontWeight: 'bold',
  },
  profitNegative: {
    color: '#c62828',
    fontWeight: 'bold',
  },
  thDate: { width: 70, fontSize: 8, fontWeight: 'bold', color: '#fff' },
  thFlex: { flex: 1, fontSize: 8, fontWeight: 'bold', color: '#fff' },
  thValue: { width: 80, fontSize: 8, fontWeight: 'bold', color: '#fff', textAlign: 'right' },
  tdDate: { width: 70, fontSize: 8 },
  tdFlex: { flex: 1, fontSize: 8 },
  tdValue: { width: 80, fontSize: 8, textAlign: 'right' },
});

export interface DrePDFProps {
  dreResult: DreResult;
  dateFrom: string;
  dateTo: string;
  taxEntries: TaxEntry[];
  logoUrl: string;
  showDetailed?: boolean;
  detailSales?: DreSaleDetailRow[];
  detailExpenses?: DreExpenseDetailRow[];
  detailMovements?: DreMovementDetailRow[];
}

const DrePDFDoc: React.FC<DrePDFProps> = ({
  dreResult,
  dateFrom,
  dateTo,
  taxEntries,
  logoUrl,
  showDetailed = false,
  detailSales = [],
  detailExpenses = [],
  detailMovements = [],
}) => {
  const formatDateBR = (s: string) => {
    const [y, m, d] = s.split('-');
    if (!y || !m || !d) return s;
    const months = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
    ];
    return `${d}/${m}/${y} (${months[parseInt(m, 10) - 1]}/${y})`;
  };

  const shortDate = (s: string) => {
    const d = (s || '').split('T')[0];
    const [y, m, day] = d.split('-');
    if (!y || !m || !day) return d || '—';
    return `${day}/${m}/${y}`;
  };

  const taxEntriesWithAmount = taxEntries.filter((e) => toNum(e.amount) > 0);
  const hasTaxas = dreResult.totalTaxas > 0;

  return (
    <Document title={`DRE - ${dateFrom} a ${dateTo}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logoUrl} style={styles.logo} />
          <View style={styles.headerRight}>
            <Text style={styles.title}>Demonstração do Resultado do Exercício</Text>
            <Text style={styles.subtitle}>
              DRE — R Motors{showDetailed ? ' (detalhada)' : ''}
            </Text>
          </View>
        </View>

        <View style={styles.period}>
          <Text style={styles.periodText}>
            Período: {formatDateBR(dateFrom)} a {formatDateBR(dateTo)}
          </Text>
          <Text style={{ fontSize: 9, marginTop: 4, color: '#555' }}>
            Veículos vendidos no período: {dreResult.veiculosVendidos}
          </Text>
        </View>

        <View style={styles.table}>
          <View style={styles.tableRowHeader}>
            <Text style={styles.tableCellHeader}>Descrição</Text>
            <Text style={styles.tableCellHeaderValue}>Valor (R$)</Text>
          </View>

          <View style={[styles.tableRow, styles.sectionTitle]}>
            <Text style={styles.tableCellDesc}>Receitas</Text>
            <Text style={styles.tableCellValue} />
          </View>
          <View style={[styles.tableRow, styles.indent1]}>
            <Text style={styles.tableCellDesc}>Faturamento bruto (valor dos veículos)</Text>
            <Text style={styles.tableCellValue}>{formatCurrency(dreResult.faturamentoBrutoVendas)}</Text>
          </View>
          <View style={[styles.tableRow, styles.indent1]}>
            <Text style={styles.tableCellDesc}>Vendas (período) — receita de referência (empresa)</Text>
            <Text style={styles.tableCellValue}>{formatCurrency(dreResult.receitaVendas)}</Text>
          </View>
          <View style={[styles.tableRow, styles.indent1]}>
            <Text style={styles.tableCellDesc}>Lucro real das vendas (período)</Text>
            <Text style={styles.tableCellValue}>{formatCurrency(dreResult.lucroRealVendas)}</Text>
          </View>
          <View style={[styles.tableRow, styles.indent1]}>
            <Text style={styles.tableCellDesc}>Entradas de movimentação de caixa</Text>
            <Text style={styles.tableCellValue}>{formatCurrency(dreResult.receitaMovimentacoes)}</Text>
          </View>
          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={styles.tableCellDesc}>Total receitas</Text>
            <Text style={styles.tableCellValue}>{formatCurrency(dreResult.totalReceitas)}</Text>
          </View>

          <View style={[styles.tableRow, styles.sectionTitle]}>
            <Text style={styles.tableCellDesc}>Despesas / Gastos</Text>
            <Text style={styles.tableCellValue} />
          </View>
          <View style={[styles.tableRow, styles.indent1]}>
            <Text style={styles.tableCellDesc}>Despesas de veículos</Text>
            <Text style={styles.tableCellValue}>{formatCurrencyDeduction(dreResult.totalDespesas)}</Text>
          </View>
          <View style={[styles.tableRow, styles.indent1]}>
            <Text style={styles.tableCellDesc}>Saídas de movimentação de caixa</Text>
            <Text style={styles.tableCellValue}>{formatCurrencyDeduction(dreResult.totalSaidasMovimentacoes)}</Text>
          </View>
          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={styles.tableCellDesc}>Total gastos</Text>
            <Text style={styles.tableCellValue}>{formatCurrencyDeduction(dreResult.totalGastos)}</Text>
          </View>

          {hasTaxas && (
            <>
              <View style={[styles.tableRow, styles.sectionTitle]}>
                <Text style={styles.tableCellDesc}>Taxas e impostos (manual)</Text>
                <Text style={styles.tableCellValue} />
              </View>
              {taxEntriesWithAmount.map((e) => (
                <View style={[styles.tableRow, styles.indent2]} key={e.id}>
                  <Text style={styles.tableCellDesc}>{e.description || '(Sem descrição)'}</Text>
                  <Text style={styles.tableCellValue}>{formatCurrencyDeduction(e.amount)}</Text>
                </View>
              ))}
              <View style={[styles.tableRow, styles.totalRow]}>
                <Text style={styles.tableCellDesc}>Total taxas e impostos</Text>
                <Text style={styles.tableCellValue}>{formatCurrencyDeduction(dreResult.totalTaxas)}</Text>
              </View>
            </>
          )}

          <View style={styles.resultRow}>
            <Text style={styles.resultLabel}>Resultado (Lucro / Prejuízo)</Text>
            <Text
              style={
                dreResult.resultado >= 0 ? styles.resultValuePositive : styles.resultValueNegative
              }
            >
              {formatCurrency(dreResult.resultado)}
            </Text>
          </View>
        </View>

        <Text style={styles.footer}>
          Documento gerado em {new Date().toLocaleString('pt-BR')} — DRE R Motors
          {showDetailed ? ' (detalhada)' : ''}
        </Text>
      </Page>

      {showDetailed && (
        <Page size="A4" style={styles.page} wrap>
          <Text style={styles.detailSectionTitle}>
            Veículos vendidos ({detailSales.length}) — lucro e gastos discriminados
          </Text>
          <Text style={{ fontSize: 8, color: '#666', marginBottom: 10 }}>
            Lucro operacional = receita da empresa − custo de compra − despesas do veículo.
          </Text>

          {detailSales.length === 0 ? (
            <Text style={{ fontSize: 9, color: '#666' }}>Nenhuma venda no período.</Text>
          ) : (
            detailSales.map((sale) => (
              <View key={sale.id} style={styles.vehicleBlock} wrap={false}>
                <View style={styles.vehicleHeader}>
                  <Text style={styles.vehicleTitle}>
                    {sale.vehicle_label}
                    {sale.plate ? ` · ${sale.plate}` : ''}
                  </Text>
                  <Text
                    style={[
                      styles.miniCellValue,
                      sale.lucro_operacional >= 0 ? styles.profitPositive : styles.profitNegative,
                    ]}
                  >
                    {formatCurrency(sale.lucro_operacional)}
                  </Text>
                </View>
                <Text style={styles.vehicleMeta}>
                  {shortDate(sale.sale_date)} · {sale.is_consignado ? 'Consignado' : 'Direto'} · Venda{' '}
                  {formatCurrency(sale.sale_price)}
                </Text>
                <View style={styles.miniRow}>
                  <Text style={styles.miniCellFlex}>Receita (empresa): {formatCurrency(sale.receita_empresa)}</Text>
                  <Text style={styles.miniCellFlex}>Custo compra: {formatCurrencyDeduction(sale.custo_compra)}</Text>
                </View>
                <View style={styles.miniRow}>
                  <Text style={styles.miniCellFlex}>
                    Despesas veículo: {formatCurrencyDeduction(sale.despesas_veiculo)}
                  </Text>
                  <Text style={styles.miniCellFlex}>
                    Lucro no caixa (período): {formatCurrency(sale.lucro_estimado)}
                  </Text>
                </View>

                <Text style={styles.miniHeader}>Gastos do veículo</Text>
                {sale.expense_items.length === 0 ? (
                  <Text style={{ fontSize: 8, color: '#888' }}>Sem despesas cadastradas.</Text>
                ) : (
                  <>
                    <View style={[styles.tableRowHeader, { marginTop: 2 }]}>
                      <Text style={styles.thDate}>Data</Text>
                      <Text style={styles.thFlex}>Categoria / Descrição</Text>
                      <Text style={styles.thValue}>Valor</Text>
                    </View>
                    {sale.expense_items.map((exp) => (
                      <View key={exp.id} style={styles.tableRow}>
                        <Text style={styles.tdDate}>{shortDate(exp.expense_date)}</Text>
                        <Text style={styles.tdFlex}>
                          {exp.category_label}
                          {exp.description ? ` — ${exp.description}` : ''}
                        </Text>
                        <Text style={styles.tdValue}>{formatCurrencyDeduction(exp.amount)}</Text>
                      </View>
                    ))}
                  </>
                )}
              </View>
            ))
          )}

          <Text style={styles.detailSectionTitle}>Despesas do período (todas)</Text>
          <View style={styles.tableRowHeader}>
            <Text style={styles.thDate}>Data</Text>
            <Text style={styles.thFlex}>Veículo / Categoria / Descrição</Text>
            <Text style={styles.thValue}>Valor</Text>
          </View>
          {detailExpenses.length === 0 ? (
            <Text style={{ fontSize: 8, color: '#888', marginTop: 4 }}>Sem despesas no período.</Text>
          ) : (
            detailExpenses.map((e) => (
              <View key={e.id} style={styles.tableRow}>
                <Text style={styles.tdDate}>{shortDate(e.expense_date)}</Text>
                <Text style={styles.tdFlex}>
                  {e.vehicle_label} · {e.category}
                  {e.description ? ` — ${e.description}` : ''}
                  {!e.affects_store_cash ? ' (não desconta caixa)' : ''}
                </Text>
                <Text style={styles.tdValue}>{formatCurrencyDeduction(e.amount)}</Text>
              </View>
            ))
          )}

          <Text style={styles.detailSectionTitle}>Movimentações de caixa do período</Text>
          <View style={styles.tableRowHeader}>
            <Text style={styles.thDate}>Data</Text>
            <Text style={styles.thFlex}>Tipo / Categoria / Descrição</Text>
            <Text style={styles.thValue}>Valor</Text>
          </View>
          {detailMovements.length === 0 ? (
            <Text style={{ fontSize: 8, color: '#888', marginTop: 4 }}>Sem movimentações no período.</Text>
          ) : (
            detailMovements.map((m) => (
              <View key={m.id} style={styles.tableRow}>
                <Text style={styles.tdDate}>{shortDate(m.date)}</Text>
                <Text style={styles.tdFlex}>
                  {m.type === 'entrada' ? 'Entrada' : 'Saída'}
                  {m.category ? ` · ${m.category}` : ''}
                  {m.description ? ` — ${m.description}` : ''}
                  {m.type === 'saida' && !m.affects_cash ? ' (pendente)' : ''}
                </Text>
                <Text style={styles.tdValue}>
                  {m.type === 'entrada' ? '+' : '−'} {formatCurrency(m.amount)}
                </Text>
              </View>
            ))
          )}

          <Text style={styles.footer}>
            Documento gerado em {new Date().toLocaleString('pt-BR')} — DRE detalhada R Motors
          </Text>
        </Page>
      )}
    </Document>
  );
};

export default DrePDFDoc;
